import { useCallback, useEffect, useState } from 'react'
import { PageHeader } from '@/shell/AppShell'
import { useToast } from '@/providers/UiProviders'
import { ErrorState, Skeleton, EmptyState } from '@/components/States'
import { ApiError } from '@/lib/api'
import { fetchFlags, resolveFlag, AM_STR as S, type ContentFlag } from './autoModApi'

const sevColor: Record<string, string> = { high: 'text-danger', medium: 'text-warning', low: 'text-tertiary' }
const statusPill: Record<string, string> = {
  pending: 'bg-warning/10 text-warning',
  dismissed: 'bg-surface-3 text-tertiary',
  actioned: 'bg-success/10 text-success',
}

export function ContentFlagsScreen() {
  const { toast } = useToast()
  const [rows, setRows] = useState<ContentFlag[] | null>(null)
  const [err, setErr] = useState<ApiError | null>(null)
  const [tick, setTick] = useState(0)
  const [status, setStatus] = useState('pending')
  const [busy, setBusy] = useState<number | null>(null)
  const reload = useCallback(() => setTick((t) => t + 1), [])

  useEffect(() => {
    const ac = new AbortController()
    setErr(null)
    fetchFlags(status, ac.signal)
      .then(setRows)
      .catch((e) => {
        if (!ac.signal.aborted) setErr(e instanceof ApiError ? e : new ApiError('INTERNAL', String(e), 500))
      })
    return () => ac.abort()
  }, [tick, status])

  const act = async (id: number, action: 'dismiss' | 'action') => {
    setBusy(id)
    try {
      await resolveFlag(id, action)
      toast(S.done, { tone: 'success' })
      reload()
    } catch {
      toast(S.failed, { tone: 'error' })
    } finally {
      setBusy(null)
    }
  }

  return (
    <>
      <PageHeader
        title={S.flags}
        screenId="A6.36"
        description={S.flagsDesc}
        actions={
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="rounded-md border border-border-default bg-surface-2 px-2 py-1 text-sm text-secondary"
          >
            <option value="pending">{S.pending}</option>
            <option value="">{S.all}</option>
            <option value="dismissed">dismissed</option>
            <option value="actioned">actioned</option>
          </select>
        }
      />
      <div className="min-h-0 flex-1 overflow-auto p-4">
        <div className="mx-auto max-w-3xl">
          {err ? (
            <ErrorState code={err.code} onRetry={reload} />
          ) : !rows ? (
            <div className="space-y-1.5">
              {Array.from({ length: 10 }).map((_, i) => (
                <Skeleton key={i} className="h-14 w-full" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <EmptyState title={S.emptyFlags} />
          ) : (
            <div className="space-y-2">
              {rows.map((f) => (
                <div key={f.id} className="rounded-lg border border-border-default bg-surface-2 p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-medium ${sevColor[f.severity] ?? ''}`}>{f.severity}</span>
                        <span className="text-xs text-tertiary">{f.category}</span>
                        <span className={`rounded px-1.5 py-0.5 text-xs ${statusPill[f.status] ?? ''}`}>{f.status}</span>
                      </div>
                      <p className="mt-1 break-words text-sm text-primary">“{f.matched_text}”</p>
                      <p className="mt-0.5 font-mono text-xs text-tertiary">
                        msg {f.message_id}
                        {f.dialog_id != null && ` · dlg ${f.dialog_id}`}
                        {f.sender_id != null && ` · user ${f.sender_id}`}
                        {` · ${new Date(f.created_at).toLocaleString()}`}
                      </p>
                    </div>
                    {f.status === 'pending' && (
                      <div className="flex shrink-0 gap-1.5">
                        <button
                          type="button"
                          disabled={busy === f.id}
                          onClick={() => act(f.id, 'action')}
                          className="rounded-md bg-danger px-2.5 py-1 text-xs text-white disabled:opacity-50"
                        >
                          {S.actionBtn}
                        </button>
                        <button
                          type="button"
                          disabled={busy === f.id}
                          onClick={() => act(f.id, 'dismiss')}
                          className="rounded-md border border-border-default px-2.5 py-1 text-xs text-secondary hover:text-primary disabled:opacity-50"
                        >
                          {S.dismiss}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  )
}
